/**
 * SKE-4681 — the one read every campaign surface shares.
 *
 * The banner, the sidebar entry, the chip and the calendar marker all call
 * this, so react-query de-duplicates them into a single request and they can
 * never disagree with each other.
 *
 * Nothing here derives the week, the row state, the snooze duration or whether
 * dismissal is allowed. The server answered all of it.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { getInstagramCampaignStatus } from "./api";
import type {
  CampaignAccountRow,
  CampaignPageRow,
  CampaignStatus,
} from "./api";

export const INSTAGRAM_CAMPAIGN_QUERY_KEY = [
  "instagram-publishing-campaign",
] as const;

/**
 * The superadmin week override, passed straight through from the URL.
 *
 * Untrusted and unvalidated on purpose: the server decides whether the caller
 * may use it, and silently ignores it for everyone else. Validating here would
 * be a second opinion on a security question.
 */
function campaignWeekFromLocation(): string | undefined {
  try {
    const value = new URLSearchParams(window.location.search).get(
      "campaignWeek"
    );
    return value ?? undefined;
  } catch {
    return undefined;
  }
}

export interface CampaignStatusView {
  status: CampaignStatus | undefined;
  isLoading: boolean;
  /** The campaign is switched on for this customer. */
  isOn: boolean;
  /** Accounts still to update. Empty once the last one is done. */
  outstanding: CampaignAccountRow[];
  /** Every Instagram account this session can see. */
  accounts: CampaignAccountRow[];
  /**
   * Every Facebook Page this session can see. A SECOND GROUP.
   *
   * Deliberately absent from `outstanding`, from `progress` and from
   * `isAffected`. A workspace whose Instagram accounts are all updated is
   * finished however many Pages are still pending, so nothing that measures
   * the campaign may read this list.
   */
  pages: CampaignPageRow[];
  /** 0 to 1. `1` when there is nothing left to do. */
  progress: number;
  /** The campaign is on, in a live week, and this customer has work to do. */
  isAffected: boolean;
}

/**
 * How often the status endpoint is re-read WHILE an authorisation pass runs.
 *
 * Two seconds, and only for the duration of the pass. The authorise route is
 * one request and one response with no progress channel, and a fifty-account
 * agency makes fifty sequential Graph calls inside it. Without this the page
 * would sit unchanged for about a minute and read as hung.
 */
export const CAMPAIGN_PASS_POLL_MS = 2000;

export interface CampaignStatusOptions {
  /**
   * True only between pressing Update now and the authorise POST settling.
   *
   * It is an option on THIS hook rather than a second hook on purpose: every
   * campaign surface already shares this query, so a parallel poll would open
   * a second request for the same answer and the two could disagree.
   */
  isPassRunning?: boolean;
}

export function useCampaignStatus({
  isPassRunning = false,
}: CampaignStatusOptions = {}): CampaignStatusView {
  const campaignWeek = campaignWeekFromLocation();

  const query = useQuery({
    queryKey: [...INSTAGRAM_CAMPAIGN_QUERY_KEY, campaignWeek ?? "real"],
    queryFn: () => getInstagramCampaignStatus(campaignWeek),
    // The answer changes when the customer finishes a pass, which invalidates
    // this key directly. Polling it harder would multiply a request every
    // campaign surface already shares.
    staleTime: 5 * 60 * 1000,
    // ...except while a pass is running, when the rows are changing under the
    // customer one at a time and there is nothing else to show them.
    refetchInterval: isPassRunning ? CAMPAIGN_PASS_POLL_MS : false,
    retry: false,
  });

  return useMemo(() => {
    const status = query.data;
    const accounts = status?.accounts ?? [];
    const pages = status?.pages ?? [];
    const outstanding = accounts.filter((row) => row.rowState !== "updated");
    /*
     * `campaign` is optional-chained as well as `status`. The contract always
     * sends it, but the SIDEBAR calls this hook on every page for every user,
     * so a 200 with an unexpected body would throw inside this memo during
     * render and take the whole app to the error boundary rather than
     * degrading to no banner. A missing field should cost the campaign, not
     * the product.
     */
    const isOn = status?.campaign?.enabled === true;
    const inLiveWeek =
      status?.campaign?.week === 1 ||
      status?.campaign?.week === 2 ||
      status?.campaign?.week === 3;

    return {
      status,
      isLoading: query.isLoading,
      isOn,
      accounts,
      pages,
      outstanding,
      progress:
        accounts.length === 0
          ? 1
          : (accounts.length - outstanding.length) / accounts.length,
      isAffected: isOn && inLiveWeek && outstanding.length > 0,
    };
  }, [query.data, query.isLoading]);
}
